import type React from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Trash2 } from 'lucide-react'
import { IconButton } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { STEP_TYPE_META } from '../stepTypeMeta.ts'
import type { TemplateStep } from '../types.ts'

export interface StepFlowNodeData {
  step: TemplateStep
  isSelected: boolean
  onSelect: () => void
  onToggleEnabled: (enabled: boolean) => void
  onDelete: () => void
}

export function StepFlowNode({ data }: NodeProps) {
  const { step, isSelected, onSelect, onToggleEnabled, onDelete } = data as unknown as StepFlowNodeData
  const meta = STEP_TYPE_META[step.step_type]
  const TypeIcon = meta.icon
  const enabled = step.config?.enabled !== false

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#9ca3af', border: '2px solid #fff', width: 10, height: 10 }}
      />
      <div
        onClick={onSelect}
        className={cn(
          'w-52 rounded-lg border-2 bg-white shadow-sm cursor-pointer transition-colors select-none',
          isSelected ? 'border-brand-500 shadow-md' : meta.borderColor,
          !enabled && 'opacity-60',
        )}
      >
        {/* Type badge */}
        <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-t-md', meta.bgColor)}>
          <TypeIcon className={cn('w-3.5 h-3.5 shrink-0', meta.textColor)} />
          <span className={cn('text-xs font-semibold flex-1 min-w-0 truncate', meta.textColor)}>
            {meta.label}
          </span>
          {step.is_required === 1 && (
            <span className="text-red-500 text-xs leading-none" title="Pflichtschritt">*</span>
          )}
        </div>

        {/* Title + description */}
        <div className="px-3 py-2">
          <p
            className={cn(
              'text-sm font-medium leading-tight',
              enabled ? 'text-gray-900' : 'text-gray-400 line-through',
            )}
          >
            {step.title}
          </p>
          {step.description && (
            <p className="text-xs text-gray-500 mt-0.5 leading-tight line-clamp-2">
              {step.description}
            </p>
          )}
        </div>

        {/* Controls */}
        <div
          className="flex items-center justify-between px-3 pb-2"
          onClick={(e) => e.stopPropagation()}
        >
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <span className="relative inline-flex shrink-0">
              <input
                type="checkbox"
                role="switch"
                checked={enabled}
                onChange={(e) => onToggleEnabled(e.target.checked)}
                className="peer sr-only"
              />
              <span className="h-4 w-7 rounded-full bg-gray-300 transition peer-checked:bg-brand-600" />
              <span className="absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform peer-checked:translate-x-3" />
            </span>
            <span className="text-xs text-gray-500">{enabled ? 'Aktiv' : 'Inaktiv'}</span>
          </label>
          <IconButton
            icon={<Trash2 className="w-3 h-3" />}
            aria-label="Schritt löschen"
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-red-600 !h-6 !w-6 !p-1"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onDelete()
            }}
          />
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#9ca3af', border: '2px solid #fff', width: 10, height: 10 }}
      />
    </>
  )
}
